import { useFormatter, useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { FC, useContext, useEffect } from "react";

import { getAvailability } from "@/components/AvailableUnitsList/action";
import { useNotification } from "@/components/NotificationProvider/NotificationProvider";
import { NotificationType } from "@/components/NotificationProvider/type";
import { getPropertyCard } from "@/components/Shortlist/action";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { LoadingContext } from "@/context/LoaderContext";
import { UserContext } from "@/context/UserContext";
import { CommercetoolsProject, FavoriteItem, PRODUCT_TYPE } from "@/types";

export interface ShortlistPopupProp {
  items: CommercetoolsProject[];
  currentFavoriteId?: string;
}

const ShortlistAddPopup: FC<ShortlistPopupProp> = ({ items, currentFavoriteId }) => {
  const item = items.find((el) => el.id === currentFavoriteId) || items[items.length - 1];

  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations();
  const { currency } = useContext(UserContext);
  const { loading } = useContext(LoadingContext);
  const { showUnavailableUnit } = useNotification();
  const handleSelectUnit = async (id: string) => {
    const card = await getPropertyCard(id);
    if (card) {
      router.push(card.cta.link.url + "#AUL-component", { scroll: true });
    }
  };
  const handleReserve = async (inventoryID: string, id: string) => {
    loading(true);
    const response = await getAvailability(inventoryID, id);
    if (response) {
      // router.push(`/${locale}/checkout`, { scroll: true });
      router.push(`/${locale}/checkout?step=contact`, { scroll: true });
    } else {
      loading(false);
      showUnavailableUnit();
      // showNotification(t("reservation.label.unitUnavailable"), 5000, NotificationType.ERROR);
    }
  };

  const format = useFormatter();

  const formatValue = (value: string | number): string | number => {
    if (typeof value === "number") {
      return value;
    }

    const cleaned = value.replace(/&/g, "").trim();
    const words = cleaned.split(/\s+/);

    return words.length === 1 ? cleaned : words.join("-");
  };

  return (
    <>
      <div className="fixed z-[100] bg-white  shadow-extra-strong w-full bottom-0 rounded-lg md:bg-gray-10 md:bottom-auto md:right-l md:top-[80px] md:w-[367px] p-m ">
        <div className="uppercase text-center font-primary text-s font-medium leading-4 tracking-widest text-black p-xs mb-m">
          {t("label.shortlist.added")}
        </div>
        <div className="rounded-t-lg bg-white p-s border-b border-gray-10 shadow-strong md:drop-shadow-none">
          <div className="flex flex-row items-start">
            <div>
              <Image
                src={item?.gallery?.[0] ?? t("noImage")}
                width={72}
                height={72}
                className="min-w-[72px] h-[72px] rounded-md aspect-1-1"
                alt={""}
              />
            </div>
            <div className="px-s text-s py-xxs">
              <div>
                <span className="font-medium">
                  {item.bedrooms}
                  {t(`filter.label.propertyType-${formatValue(item?.propertyType?.[0] || item?.identifier || "") as string}`)}
                </span>
              </div>
              <div className="my-s">{[item.project, item.area, item.city].join(", ")}</div>
              <div className="flex flex-row justify-between">
                <div>{item.type === "unit-type" ? item.name : " "}</div>
                <div className="font-medium">
                  {format.number(Number(item.priceMin) / 100 || Number(item.price) || 0, {
                    style: "currency",
                    currency: currency,
                    trailingZeroDisplay: "stripIfInteger",
                    maximumFractionDigits: 0,
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="rounded-b-lg px-s bg-white flex flex-row justify-between py-m cursor-pointer"
          onClick={() => {
            item.type === PRODUCT_TYPE.PROPERTY_TYPE && handleSelectUnit(item.id);
            item.type === PRODUCT_TYPE.UNIT_TYPE && handleReserve(item.inventoryID, item.id);
          }}
        >
          <div className="underline font-secondary font-medium text-s">
            {item.type === PRODUCT_TYPE.PROPERTY_TYPE && t("label.selectSpecificUnit")}
            {item.type === PRODUCT_TYPE.UNIT_TYPE && t("label.reserve")}
          </div>
          <div>
            <Icon size={"16"} name={"chevron-right"} className="stroke-black" />
          </div>
        </div>
        <div className="pt-m">
          <Button
            size={ButtonSize.MEDIUM}
            theme={ButtonTheme.BLACK}
            customClass="uppercase w-full"
            type="button"
            variant={ButtonVariant.PRIMARY}
            onClick={() => router.push(`/${locale}/shortlist`, { scroll: true })}
          >
            {t("label.shortlist.viewAndCompare")}
          </Button>
        </div>
      </div>
    </>
  );
};
export default ShortlistAddPopup;
